import React, { useRef, useState, useCallback } from "react";

// Bảng dùng chung cho cả project:
//  - Kéo giãn độ rộng từng cột (như Excel), có thể lưu vào localStorage.
//  - Đóng băng hàng tiêu đề (sticky header) khi cuộn dọc.
//  - Đóng băng cột (sticky) khi cuộn ngang (đặt column.sticky = true cho các
//    cột đầu).
//
// Cách dùng:
//   <ResizableTable
//     columns={[
//       { key: "name", header: "Họ tên", width: 200, sticky: true, align: "left",
//         render: (row) => <b>{row.name}</b> },
//       { key: "phone", header: "SĐT", width: 130, render: (row) => row.phone },
//     ]}
//     rows={list}
//     rowKey={(row) => row.id}
//     fullRowRender={(row) => editingId === row.id ? <Form/> : null} // tuỳ chọn
//     maxHeight="70vh"
//     storageKey="staff-all"     // tuỳ chọn: nhớ độ rộng cột
//   />

const DEFAULT_WIDTH = 140;
const MIN_WIDTH = 50;

function loadWidths(storageKey) {
  if (!storageKey) return {};
  try {
    return JSON.parse(localStorage.getItem("rt_widths_" + storageKey) || "{}") || {};
  } catch {
    return {};
  }
}

function ResizeHandle({ onResize, onCommit }) {
  const drag = useRef(null);
  const onMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    drag.current = { lastX: e.clientX };
    const move = (ev) => {
      if (!drag.current) return;
      const delta = ev.clientX - drag.current.lastX;
      drag.current.lastX = ev.clientX;
      onResize(delta);
    };
    const up = () => {
      drag.current = null;
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      if (onCommit) onCommit();
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };
  return (
    <div
      onMouseDown={onMouseDown}
      title="Kéo để đổi độ rộng cột"
      style={{ position: "absolute", top: 0, right: 0, bottom: 0, width: 6, cursor: "col-resize", userSelect: "none" }}
    />
  );
}

export default function ResizableTable({
  columns,
  rows,
  rowKey,
  fullRowRender,
  maxHeight = "70vh",
  storageKey,
  headerBg = "#f9fafb",
  cellBg = "#ffffff",
}) {
  const [widths, setWidths] = useState(() => {
    const saved = loadWidths(storageKey);
    const init = {};
    columns.forEach((c) => {
      init[c.key] = saved[c.key] || c.width || DEFAULT_WIDTH;
    });
    return init;
  });

  const widthOf = (c) => widths[c.key] || c.width || DEFAULT_WIDTH;

  const resize = useCallback((key, minWidth, delta) => {
    setWidths((w) => ({ ...w, [key]: Math.max(minWidth || MIN_WIDTH, (w[key] || DEFAULT_WIDTH) + delta) }));
  }, []);

  const commit = useCallback(() => {
    if (!storageKey) return;
    setWidths((w) => {
      try {
        localStorage.setItem("rt_widths_" + storageKey, JSON.stringify(w));
      } catch {
        /* bỏ qua nếu localStorage đầy/không dùng được */
      }
      return w;
    });
  }, [storageKey]);

  // left offset tích luỹ cho các cột sticky ở đầu
  const stickyLeft = {};
  let acc = 0;
  columns.forEach((c) => {
    if (c.sticky) {
      stickyLeft[c.key] = acc;
      acc += widthOf(c);
    }
  });

  const totalWidth = columns.reduce((sum, c) => sum + widthOf(c), 0);

  const baseCell = { border: "1px solid #e5e7eb", padding: "6px 8px", fontSize: 13, boxSizing: "border-box", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };

  return (
    <div style={{ overflow: "auto", maxHeight, border: "1px solid #e5e7eb", borderRadius: 8 }}>
      <table style={{ borderCollapse: "collapse", tableLayout: "fixed", width: totalWidth }}>
        <colgroup>
          {columns.map((c) => (
            <col key={c.key} style={{ width: widthOf(c) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {columns.map((c) => {
              const th = {
                ...baseCell,
                background: headerBg,
                fontWeight: 700,
                textAlign: c.headerAlign || c.align || "left",
                position: "sticky",
                top: 0,
                zIndex: c.sticky ? 3 : 2,
                ...(c.sticky ? { left: stickyLeft[c.key] } : {}),
              };
              return (
                <th key={c.key} style={{ ...th, position: "sticky" }}>
                  <div style={{ position: "relative" }}>
                    {c.header}
                    <ResizeHandle onResize={(d) => resize(c.key, c.minWidth, d)} onCommit={commit} />
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const full = fullRowRender ? fullRowRender(row) : null;
            if (full) {
              // Hàng mở rộng (vd form sửa) — không giới hạn nowrap/ellipsis.
              return (
                <tr key={rowKey(row)}>
                  <td colSpan={columns.length} style={{ border: "1px solid #e5e7eb", padding: "6px 8px" }}>
                    {full}
                  </td>
                </tr>
              );
            }
            return (
              <tr key={rowKey(row)}>
                {columns.map((c) => {
                  const td = {
                    ...baseCell,
                    textAlign: c.align || "left",
                    ...(c.sticky ? { position: "sticky", left: stickyLeft[c.key], zIndex: 1, background: cellBg } : {}),
                  };
                  return (
                    <td key={c.key} style={td}>
                      {c.render ? c.render(row, rowIndex) : row[c.key]}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
